import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { Address } from '@models/address';
import { HolderResidenceAddress, MailingAddress, PatrimonialAddress, PatrimonialDataChangeCustomConfigKey,
  PatrimonialDataChangeEnabledConfig, PatrimonialDataChangeEnabledConfigKeys, PatrimonialDataChangeState
} from '@models/patrimonial';
import { PolicyAccident } from '@models/policy-accident';
import { PolicyDeath } from '@models/policy-death';
import { PolicyHome } from '@models/policy-home';
import { PolicyOther } from '@models/policy-other';
import { ValidatorMobile } from '@utils/custom-validators';
import { StoreUtils } from '@utils/store';

@Injectable({
  providedIn: 'root'
})
export class PatrimonialDataChangeHelperService extends StoreUtils<PatrimonialDataChangeState> {

  mailingAddressMap: Map<string, Partial<MailingAddress>>;
  holderResidenceMap: Map<string, Partial<HolderResidenceAddress>>;
  private readonly customConfigKey: string;

  constructor(
    private mmobileHelper: MMobileHelperService
  ) {
    super();
    this.holderResidenceMap = new Map();
    this.mailingAddressMap = new Map();
    this.setState({ mailingAddress: {}, holderResidenceAddress: {} });
    this.customConfigKey = PatrimonialDataChangeCustomConfigKey;
  }

  createUserDataForm(control: 'name' | 'phone') {
    return new FormGroup({
      [control]: new FormControl('', control === 'name'
        ? [Validators.required]
        : [Validators.required, ValidatorMobile])
    });
  }

  isEnabledPatrimonialDataChange(
    policy: PolicyHome | PolicyDeath | PolicyAccident | PolicyOther,
    type: PatrimonialDataChangeEnabledConfigKeys): boolean {
    const policyDataChangeConfig: PatrimonialDataChangeEnabledConfig[] = this.getPatrimonialDataChangeConfig(type);
    const companyId: string = this.getCompanyId(policy);
    const config: PatrimonialDataChangeEnabledConfig =
      (policyDataChangeConfig || []).find(e => this.removeFirstZeros(e.companyId) === companyId);
    if (!config) {
      return false;
    }
    if (config?.department.length === 0 && config.online) {
      return true;
    }
    const department = this.getDepartment(policy);
    const departments = this.getDepartaments(config);
    return departments.includes(department) && config.online;
  }

  getAddress(data: PatrimonialAddress, country: string): Address {
    if (!country) {
      country = this.getCountry(data);
    }
    return data ? {
      country,
      postalCode: data.postalCode,
      province: data.province,
      streetType: this.getStreetType(data),
      street: data.street || data.viaName,
      number: data.number,
      city: data.city || data.location,
      portal: data.portal,
      stair: data.stairs,
      door: data.door,
      floor: data.floor || data.flat,
      internationalAddress: data.internationalAddress,
      additionalInfo: data.additionalInfo || data.complement
    } as Address
      : {} as Address;
  }

  setMailingAddressMap(state: Partial<PatrimonialDataChangeState>) {
    const arr = state.mailingAddress ? Object.keys(state.mailingAddress) : [];
    this.mailingAddressMap = new Map();
    arr.forEach((id) => {
      this.mailingAddressMap.set(id, state.mailingAddress[id]);
    });
  }

  setHolderResidenceAddressMap(state: Partial<PatrimonialDataChangeState>) {
    const arr = state.holderResidenceAddress ? Object.keys(state.holderResidenceAddress) : [];
    this.holderResidenceMap = new Map();
    arr.forEach((id) => {
      this.holderResidenceMap.set(id, state.holderResidenceAddress[id]);
    });
  }

  updateSendCorrespondenceData(
    state: Partial<PatrimonialDataChangeState>,
    value: Address,
    name: string
  ) {
    const residence = this.buildResidence(value);
    const data = value.country === 'ES'
      ? { ...value, name, residence }
      : {
        internationalAddress: value.internationalAddress,
        countryName: value.countryName,
        country: value.country,
        name,
        residence
      };

    if (!state.mailingAddress) {
      state.mailingAddress = {};
    }
    state.mailingAddress[this.policy.policyNumber] = data;
    this.updateState(state);
  }

  updateHolderResidencePhone(
    state: Partial<PatrimonialDataChangeState>,
    addressStored: Partial<HolderResidenceAddress>,
    phone: string) {
    if (!addressStored) {
      addressStored = {};
    }
    if (!state.holderResidenceAddress) {
      state.holderResidenceAddress = {};
    }
    state.holderResidenceAddress[this.policy.policyNumber] = { ...addressStored, phone, phoneUpdated: true };
    this.updateState(state);
  }

  updateHolderResidenceData(
    state: Partial<PatrimonialDataChangeState>,
    value: Address,
    phone: string,
    phoneUpdated: boolean = false
  ) {
    const residence = this.buildResidence(value);
    const data = value.country === 'ES'
      ? { ...value, phone, residence, residenceUpdated: true, phoneUpdated }
      : {
        internationalAddress: value.internationalAddress,
        countryName: value.countryName,
        country: value.country,
        phone,
        residence,
        residenceUpdated: true,
        phoneUpdated
      };

    state.holderResidenceAddress[this.policy.policyNumber] = data;
    this.updateState(state);
  }


  buildResidence(address: Address): string {
    const country = this.getCountry(address);
    if (country !== 'ES') {
      return `${address.internationalAddress || address.street}${address.countryName || ''}`;
    }

    return `${address.streetType || ''}
      ${address.street || ''}
      ${address.number || ''}
      ${address.portal || ''}
      ${address.stair || ''}
      ${address.floor || ''}
      ${address.door || ''}
      ${address.additionalInfo || ''}
      ${address.city || ''}
      ${address.postalCode || ''}
      ${address.province || ''}
      ${address.countryName || ''}
      ${address.countryCode || ''}`;
  }

  getCountry(address: Address): string {
    if (address.country) {
      return address.country;
    }
    if (address.countryCode) {
      return address.countryCode;
    }
    const clone = JSON.parse(JSON.stringify(address));
    delete clone.name;
    return Object.keys(clone).length > 1 ? 'ES' : 'Otro';
  }

  private getStreetType(data: PatrimonialAddress) {
    let streetType = data.viaType || data.streetType;

    if (streetType === 'CL') {
      streetType = 'CLL';
      return streetType;
    }

    if (streetType === 'BL') {
      streetType = 'BLQ';
      return streetType;
    }

    if (streetType === 'CM') {
      streetType = 'CNO';
      return streetType;
    }

    if (streetType === 'ROT') {
      streetType = 'RTD';
      return streetType;
    }

    if (streetType === 'APE' || streetType === 'APN') {
      streetType = 'APD';
      return streetType;
    }

    return streetType;
  }

  private getPatrimonialDataChangeConfig(type: PatrimonialDataChangeEnabledConfigKeys): PatrimonialDataChangeEnabledConfig[] {
    return this.mmobileHelper.getCustomConfigData<PatrimonialDataChangeEnabledConfig[]>(
      this.customConfigKey,
      type
    );
  }

  private getDepartaments(config: PatrimonialDataChangeEnabledConfig): string[] {
    return config?.department.map(d => this.removeFirstZeros(d));
  }

  private getDepartment(policy: PolicyHome | PolicyDeath | PolicyAccident | PolicyOther): string {
    return this.removeFirstZeros(policy['department'] || policy['productCode']);
  }

  private getCompanyId(policy: PolicyHome | PolicyDeath | PolicyAccident | PolicyOther): string {
    return this.removeFirstZeros(policy.companyId || policy['entityCode']);
  }

  private removeFirstZeros(value: string): string {
    return Number(value).toString();
  }
}
